import { CartAddEvent } from '@theme/events';

/**
 * <lp-cart-upsell> — LP-CART-BARBERS-V1
 *
 * Agrega un complemento al carrito sin salir del drawer. El markup lo renderiza
 * Liquid (snippets/lp-cart-upsell.liquid), así que después del morph el bloque
 * vuelve solo, ya sin el producto recién agregado: acá no se inyecta nada.
 *
 * Mecanismo (Horizon de lilipro.cl): POST a cart_add_url pidiendo las sections de
 * los <cart-items-component> presentes y luego CartAddEvent con esas sections. El
 * cart-items-component escucha ese evento en document y hace morph del drawer, que
 * es exactamente lo que hace product-form.js al agregar desde una ficha.
 *
 * La línea se marca con la propiedad `_origen: cart-upsell` para poder medir después
 * qué vendió el carrito (mismo criterio que en PET, py/reportes/reporte_upsell.py).
 */
class LpCartUpsell extends HTMLElement {
  #observer = null;
  #viewSent = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#observeView();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  get #bloque() {
    return this.dataset.bloque || 'complementarios';
  }

  /** La analítica nunca puede romper el carrito: todo dentro de try/catch. */
  #track(nombre, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', nombre, params);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: nombre, ...params });
      }
    } catch (_) {
      /* no-op */
    }
  }

  /**
   * `lp_upsell_view` la primera vez que el bloque queda a la vista. Con el drawer
   * cerrado el elemento no tiene caja, así que una "vista" = el carrito se abrió y
   * el bloque quedó visible.
   */
  #observeView() {
    if (this.#viewSent || !this.querySelector('[data-lp-add]')) return;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.#viewSent) continue;
          this.#viewSent = true;
          this.#observer?.disconnect();

          const botones = [...this.querySelectorAll('[data-lp-add]')];
          this.#track('lp_upsell_view', {
            marca: 'barbers',
            bloque: this.#bloque,
            tarjetas: botones.length,
            skus: botones
              .map((b) => b.getAttribute('data-sku') || '')
              .filter(Boolean)
              .join(','),
          });
        }
      },
      { threshold: 0.2 }
    );
    this.#observer.observe(this);
  }

  #onClick = async (event) => {
    const btn = event.target.closest('[data-lp-add]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    // El clic se mide antes del fetch: interesa la intención, no si el add terminó.
    const tarjetas = [...this.querySelectorAll('[data-lp-add]')];
    this.#track('lp_upsell_click', {
      marca: 'barbers',
      bloque: this.#bloque,
      sku: btn.getAttribute('data-sku') || '',
      variant_id: variantId,
      titulo: btn.getAttribute('data-titulo') || '',
      posicion: tarjetas.indexOf(btn) + 1,
      value: Number(btn.getAttribute('data-precio') || 0),
      currency: 'CLP',
    });

    btn.disabled = true;
    btn.classList.add('is-loading');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_origen]', 'cart-upsell');
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add';

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || `add ${data.status}`);

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'lp-cart-upsell', {
          source: 'lp-cart-upsell',
          itemCount: 1,
          productId: btn.getAttribute('data-product-id') || undefined,
          variantId,
          sections: data.sections,
        })
      );
    } catch (e) {
      console.error('[lp-cart-upsell]', e);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  };
}

if (!customElements.get('lp-cart-upsell')) {
  customElements.define('lp-cart-upsell', LpCartUpsell);
}
